import { Component, OnDestroy } from '@angular/core'
import { MatSnackBar } from '@angular/material/snack-bar'
import { Router } from '@angular/router'
import { MsalBroadcastService, MsalService } from '@azure/msal-angular'
import { AuthenticationResult, EventMessage, EventType } from '@azure/msal-browser'
import { Select, Store } from '@ngxs/store'
import {
  auditTime,
  distinctUntilChanged,
  filter,
  first,
  firstValueFrom,
  Observable,
  Subject,
  takeUntil,
} from 'rxjs'
import { UserService } from './services/user.service'
import { SignalingConfigService } from './services/signaling-config.service'
import { SignalingDebugService } from './services/signaling-debug.service'
import { AppActions } from './shared/store/app.actions'
import { AppState } from './shared/store/app.state'
import { UserAction } from './shared/store/user/user.actions'
import { UserStateModel } from './shared/store/user/user.state'

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnDestroy {
  public isLoading$!: Observable<boolean>

  #ngUnsubscribe$: Subject<void> = new Subject()

  // @Select(AppState.isLoadingUrls) isLoading$!: Observable<boolean>
  @Select(AppState.error) error$!: Observable<{
    hasError: boolean
    errorMessage: string
  }>

  private get userLoggedIn(): boolean {
    return this.store.selectSnapshot((state) => !!state.app.user.crmUserId)
  }

  constructor(
    private readonly authService: MsalService,
    private readonly userService: UserService,
    private readonly broadcastService: MsalBroadcastService,
    private readonly store: Store,
    private readonly snackbar: MatSnackBar,
    private readonly signalingConfigService: SignalingConfigService,
    private readonly signalingDebugService: SignalingDebugService,
  ) {
    this.isLoading$ = this.store
      .select(AppState.isLoadingUrls)
      .pipe(auditTime(250), distinctUntilChanged())

    this.error$.pipe(takeUntil(this.#ngUnsubscribe$)).subscribe(({ hasError, errorMessage }) => {
      if (!hasError) return

      const snackbarRef = this.snackbar.open(errorMessage, undefined, {
        duration: 15000,
        panelClass: 'c-error-toast',
      })
      snackbarRef
        .afterDismissed()
        .pipe(first())
        .subscribe(() => {
          this.store.dispatch(new AppActions.ClearError())
        })
    })
    this.broadcastService.msalSubject$
      .pipe(
        filter((m: EventMessage) =>
          [EventType.ACQUIRE_TOKEN_SUCCESS, EventType.ACQUIRE_TOKEN_FAILURE].includes(m.eventType)
        ),
        takeUntil(this.#ngUnsubscribe$)
      )

    this.logSignalingReadiness()
  }

  private logSignalingReadiness(): void {
    void this.signalingDebugService
    console.info('[Signaling] readiness smoke check', this.signalingConfigService.getReadiness())
  }

  async #login(): Promise<AuthenticationResult | void> {
    try {
      if (this.userLoggedIn) return Promise.resolve()
      const result = await firstValueFrom(this.authService.loginPopup())
      const {
        displayName: fullName = '',
        givenName: firstName = '',
        surname: lastName = '',
        mail = '',
      } = await this.userService.getUserDetails()
      const { email, crmUserId, colorPreference, headshot } = await this.userService.getUserByEmail(
        mail
      )
      const user: Partial<UserStateModel> = {
        colorPreference,
        crmUserId,
        email,
        headshot,
        firstName,
        lastName,
        initials: `${firstName.slice(0, 1).toUpperCase()}${lastName.slice(0, 1).toUpperCase()}`,
        fullName,
      }
      this.store.dispatch(new UserAction.Update(user))
      const breeds = await this.userService.getBreedList()
      return result
    } catch (err) {
      return Promise.reject(err)
    }
  }

  #setActiveAccount(): void {
    const activeAccount = this.authService.instance.getActiveAccount()
    const allAccounts = this.authService.instance.getAllAccounts()
    if (!activeAccount && !allAccounts?.length) {
      throw new Error('No Accounts Found')
    }
    this.authService.instance.setActiveAccount(activeAccount || allAccounts[0])
  }

  ngOnDestroy(): void {
    this.#ngUnsubscribe$.next()
    this.#ngUnsubscribe$.unsubscribe()
  }
}
